import Axios from "axios";
import { setupCache } from "axios-cache-interceptor";
import { CACHE_EXPIRATION_TIME } from "./config/config.json";

/**
 * Options for caching API requests.
 *
 * @typedef {Object} CacheOptions
 * @property {Function} debug - The debug function to use for logging cache-related messages.
 * @property {Array<string>} methods - The HTTP methods to cache.
 * @property {number} ttl - The time-to-live (expiration time) for cached responses.
 */

/**
 * Cache options for API requests.
 *
 * @type {CacheOptions}
 */
const cacheOptions = {
  debug: console.warn,
  methods: ["get"],
  ttl: CACHE_EXPIRATION_TIME
};
const instance = Axios.create(); // Create a new Axios instance
const axios = setupCache(instance, cacheOptions); // Setup cache with Axios instance

axios.interceptors.request.use(
  function (config) {
    // You can add other headers here too
    // For example, Authorization header if needed
    config.headers.version = "2.0";

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  // Intercept the response
  (response) => {
    console.debug("WebClient:: Fetch response: ", response);
    if (!response.statusText === "OK") {
      const error = (response && {
        message: response.message,
        status: response.status
      }) || {
        message: response.statusText,
        status: response.status
      };
      return Promise.reject(error);
    }
    return Promise.resolve(response.data);
  },
  (error) => {
    console.error("WebClient:: Response error: ", error);
    if (error.response && error.response.status === 401) {
      console.error("WebClient:: 401 error: Unauthorized access", error);
      window.dispatchEvent(new CustomEvent("unauthorized", { detail: error }));
      // Handle 401 error, e.g., redirect to login or refresh token
    } else if (error.status === 403) {
      console.error("WebClient:: 403 error: Restricted access", error);
      window.dispatchEvent(new CustomEvent("restrict_access", { detail: error }));
    }
    return Promise.reject(error);
  }
);

/**
 * Sends a GET request to the specified URL.
 *
 * @param {string} url - The URL to send the GET request to.
 * @param {Object} requestOptions - Optional request options.
 * @returns {Promise} - A Promise that resolves to the response data.
 */
function get(url, requestOptions = {}) {
  // debugger;
  return axios.get(url, requestOptions);
}

/**
 * Sends a POST request to the specified URL with the given body and request options.
 *
 * @param {string} url - The URL to send the request to.
 * @param {object} requestOptions - The options for the request (e.g., headers, timeout).
 * @param {object} body - The body of the request.
 * @returns {Promise} - A promise that resolves to the response from the server.
 */
function post(url, requestOptions, body) {
  return axios.post(url, body, requestOptions);
}

/**
 * Sends a PUT request to the specified URL with the provided request options and body.
 *
 * @param {string} url - The URL to send the PUT request to.
 * @param {object} requestOptions - The options for the request (e.g., headers, query parameters).
 * @param {object} body - The body of the request.
 * @returns {Promise} - A Promise that resolves to the response from the server.
 */
function put(url, requestOptions, body) {
  return axios.put(url, body, requestOptions);
}

// prefixed with underscored because delete is a reserved word in javascript
/**
 * Sends a DELETE request to the specified URL using Axios.
 *
 * @param {string} url - The URL to send the DELETE request to.
 * @param {object} requestOptions - Additional options for the request (e.g., headers, timeout).
 * @param {object} body - The request body to be sent as JSON.
 * @returns {Promise} - A Promise that resolves to the Axios response.
 */
function _delete(url, requestOptions, body) {
  return axios.delete(url, { data: body, headers: requestOptions });
}
/**
 * @deprecated This method will be deleted in newer version. Use post instead.
 */
function postWithOption(url, requestOptions, body) {
  console.warn("WebClient:: postWithOption is deprecated. Use post instead.");
  return post(url, requestOptions, body);
}

/**
 * @deprecated This method will be deleted in newer version. Use post instead.
 */
function postFormData(url, requestOptions, body) {
  console.warn("WebClient:: postFormData is deprecated. Use post instead.");
  return post(url, requestOptions, body);
}

/**
 * @deprecated This method will be deleted in newer version. Use get instead.
 */
function getBlobData(url, requestOptions = {}) {
  console.warn("WebClient:: getBlobData is deprecated. Use get instead.");
  return get(url, requestOptions);
}

/**
 * @deprecated This method will be deleted in newer version. Use get instead.
 */
function getCache(url, requestOptions = {}) {
  console.warn("WebClient:: getCache is deprecated. Use get instead.");
  return get(url, requestOptions);
}

/**
 * WebClient object for making HTTP requests.
 *
 * @typedef {Object} WebClient
 * @property {Function} get - HTTP GET request.
 * @property {Function} post - HTTP POST request.
 * @property {Function} put - HTTP PUT request.
 * @property {Function} delete - HTTP DELETE request.
 * @property {Function} postWithOption - Deprecated - HTTP POST request with options.
 * @property {Function} postFormData - Deprecated - HTTP POST request with form data.
 * @property {Function} getBlobData - Deprecated - HTTP GET request for blob data.
 * @property {Function} getCache - Deprecated - HTTP GET request with caching.
 */
export const WebClient = {
  get,
  post,
  put,
  delete: _delete,
  postWithOption,
  postFormData,
  getBlobData,
  getCache
};
